import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { useSEO } from '../hooks/useSEO';

const ChangePasswordPage = () => {
  useSEO({
    title: "Change Password | AM CARDMATE",
    description: "Update your account password for enhanced security. Keep your AM CARDMATE account safe and secure.",
    keywords: "change password, update password, account security, password change"
  });

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Change Password",
      "description": "Update your account password",
      "url": "https://amcardmate.com/change-password",
      "isPartOf": {
        "@type": "WebSite",
        "name": "AM CARDMATE"
      }
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  }, []);

  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters long';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSuccess(false);

    try {
      const result = await authService.changePassword(formData.currentPassword, formData.newPassword);
      
      if (result.error) {
        if (result.error.includes('wrong-password') || result.error.includes('invalid-credential')) {
          setErrors({ currentPassword: 'Current password is incorrect' });
        } else {
          setErrors({ general: result.error });
        }
      } else {
        setSuccess(true);
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        setTimeout(() => {
          navigate('/account?tab=settings');
        }, 2000);
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 sm:py-12 px-4 sm:px-6">
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />
      
      <div className="max-w-md mx-auto relative z-10 pt-20 md:pt-24 lg:pt-32">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <FontAwesomeIcon icon="key" className="text-white text-2xl" />
          </div>
          <h1 className="text-h3 text-dark mb-2">
            Change <span className="text-gradient-blue-pink">Password</span>
          </h1>
          <p className="text-body text-gray-600">Update your account password</p>
        </div>

        {/* Form Card */}
        <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl p-6 sm:p-8">
          {/* Success Message */}
          {success && (
            <div className="bg-green-50 rounded-2xl p-4 mb-6 border border-green-200">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="check-circle" className="text-green-600" />
                <p className="text-body font-medium text-green-800">Password changed successfully!</p>
              </div>
              <p className="text-body-sm text-green-700 mt-1">Redirecting to account settings...</p>
            </div>
          )}

          {/* Error Message */}
          {errors.general && (
            <div className="bg-red-50 rounded-2xl p-4 mb-6 border border-red-200">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="exclamation-triangle" className="text-red-500" />
                <p className="text-body-sm text-red-600">{errors.general}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Current Password */}
            <div>
              <label className="block text-body-sm font-medium text-gray-700 mb-2">
                Current Password *
              </label>
              <div className="relative">
                <input
                  type={showPasswords.current ? "text" : "password"}
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 pr-12 rounded-2xl transition-all duration-300 text-body ${
                    errors.currentPassword
                      ? 'bg-red-50 border-2 border-red-200 focus:border-red-400'
                      : 'bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white'
                  }`}
                  placeholder="Enter your current password"
                  required
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FontAwesomeIcon icon={showPasswords.current ? "eye-slash" : "eye"} />
                </button>
              </div>
              {errors.currentPassword && (
                <p className="text-body-sm text-red-500 mt-1">{errors.currentPassword}</p>
              )}
            </div>

            {/* New Password */}
            <div>
              <label className="block text-body-sm font-medium text-gray-700 mb-2">
                New Password *
              </label>
              <div className="relative">
                <input
                  type={showPasswords.new ? "text" : "password"}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 pr-12 rounded-2xl transition-all duration-300 text-body ${
                    errors.newPassword
                      ? 'bg-red-50 border-2 border-red-200 focus:border-red-400'
                      : 'bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white'
                  }`}
                  placeholder="Enter your new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FontAwesomeIcon icon={showPasswords.new ? "eye-slash" : "eye"} />
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-body-sm text-red-500 mt-1">{errors.newPassword}</p>
              )}
              <p className="text-caption text-gray-500 mt-1">
                Password must be at least 6 characters long
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-body-sm font-medium text-gray-700 mb-2">
                Confirm New Password *
              </label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 pr-12 rounded-2xl transition-all duration-300 text-body ${
                    errors.confirmPassword
                      ? 'bg-red-50 border-2 border-red-200 focus:border-red-400'
                      : 'bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white'
                  }`}
                  placeholder="Confirm your new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FontAwesomeIcon icon={showPasswords.confirm ? "eye-slash" : "eye"} />
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-body-sm text-red-500 mt-1">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || success}
              className="w-full btn btn-primary btn-lg"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon="spinner" className="animate-spin" />
                  <span>Changing Password...</span>
                </>
              ) : success ? (
                <>
                  <FontAwesomeIcon icon="check" />
                  <span>Password Changed!</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="key" />
                  <span>Change Password</span>
                </>
              )}
            </button>
          </form>

          {/* Security Tips */}
          <div className="mt-6 p-4 bg-blue-50 rounded-2xl border border-blue-200">
            <h4 className="text-body font-semibold text-blue-800 mb-2">
              <FontAwesomeIcon icon="shield-alt" className="mr-2" />
              Password Security Tips
            </h4>
            <ul className="text-body-sm text-blue-700 space-y-1">
              <li>• Use a mix of letters, numbers, and symbols</li>
              <li>• Make it at least 8 characters long</li>
              <li>• Don't reuse passwords from other accounts</li>
              <li>• Consider using a password manager</li>
            </ul>
          </div>

          {/* Back Link */}
          <div className="mt-6 text-center">
            <Link 
              to="/account?tab=settings" 
              className="text-body-sm text-gray-500 hover:text-gray-700 transition-colors inline-flex items-center gap-1"
            >
              <FontAwesomeIcon icon="arrow-left" />
              Back to Account Settings
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;